import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as DataActionCreators from "../../../actions/data.actions";
import * as DashboardActionCreators from "../../../actions/dashboard.actions";
import * as UserActionCreators from "../../../actions/user.actions";
import * as DTOS from "../../../FeralScan.dtos";
import Loader from 'react-loaders';
import { LocalForm, Control } from 'react-redux-form';
import { ReferenceDataState } from '../../../reducers/referenceData.reducer';
import PlotBadgeContainer from './PlotBadgeContainer';
import FileImageThumbnail from '../../File/FileImageThumbnail';
import PlotPhotoUpload from './PlotPhotoUpload';
import ModalMessage from '../../Common/Modal';
import { IAppState } from '../../../app';

type PlotAdminEditProps = {
    plot: DTOS.Plot;
}

type PlotAdminEditActions = {
}

type PlotAdminEditPropsMapped = {
    plotPhotoUploading: boolean,
    saving: boolean;
    referenceData: ReferenceDataState;
}

type PlotAdminEditActionsMapped = {
    updatePlot: typeof DashboardActionCreators.updatePlotRequest;
    uploadPlotPhotos: typeof DashboardActionCreators.uploadPlotPhotosRequest;
}

type PlotAdminEditAllPropsInherited = PlotAdminEditProps & PlotAdminEditActions;
type PlotAdminEditAllPropsMapped = PlotAdminEditPropsMapped & PlotAdminEditActionsMapped;

type PlotAdminEditAllProps = Partial<PlotAdminEditAllPropsMapped> & PlotAdminEditAllPropsInherited;

interface IPlotAdminEditState {
    deleteUserRole: DTOS.UserRole;
    startDate: any;
    endDate: any;
    showDeletePlotModal: boolean;
    showDeleteUserRoleModal: boolean;
    plotReadyToSave: any;
}

class PlotAdminEdit extends React.Component<PlotAdminEditAllProps, IPlotAdminEditState> {
    constructor(props, context) {
        super(props, context);

        this.state = {
            startDate: undefined,
            endDate: undefined,
            showDeletePlotModal: false,
            showDeleteUserRoleModal: false,
            plotReadyToSave: undefined,
            deleteUserRole: undefined,
        }

        this.onDeletePlotHideModel = this.onDeletePlotHideModel.bind(this);
        this.onDeletePlotItem = this.onDeletePlotItem.bind(this);
    }

    onDeletePlotHideModel() {
        this.setState({
            showDeletePlotModal: false,
            plotReadyToSave: undefined
        });
    }

    onDeletePlotItem() {
        this.savePlot(this.state.plotReadyToSave);

        this.setState({
            showDeletePlotModal: false,
            plotReadyToSave: undefined
        });
    }

    onSubmit(plot) {
        if (!this.props.plot.obsolete && plot.obsolete) {
            this.setState({
                showDeletePlotModal: true,
                plotReadyToSave: plot
            });
        } else {
            this.savePlot(plot);
        }
    }

    savePlot(plot) {
        if (this.props.plot && plot) {
            // copy from props to make sure we have the correct plot ID and don't change any details we don't intend to
            var submitPlot = { ...this.props.plot }

            var plotStatus = submitPlot.plotStatus;

            if (plot.setNotEvaluating) {
                plotStatus = this.props.referenceData && this.props.referenceData.plotStatuses
                    ? { ...this.props.referenceData.plotStatuses.find(p => p.code == "INACTIVE") }
                    : submitPlot.plotStatus;
            }

            submitPlot.apcCode = plot.apcCode;
            submitPlot.nearestSuburb = plot.nearestSuburb;
            submitPlot.waterBody = plot.waterBody;
            submitPlot.name = plot.name;
            submitPlot.latitude = plot.latitude;
            submitPlot.longitude = plot.longitude;
            submitPlot.private = plot.private;
            submitPlot.obsolete = plot.obsolete;
            submitPlot.plotStatus = plotStatus;

            this.props.updatePlot(submitPlot);
        }
    }

    renderFormRow(label: string, formComponent: any) {
        return <div className="form-group row py-2 border-bottom">
            <label className="col-md-3">{label}</label>
            <div className="col-md-9">
                {formComponent}
            </div>
        </div>
    }

    render() {

        const { plot, plotPhotoUploading, saving, uploadPlotPhotos } = this.props;

        const statusBadge = <PlotBadgeContainer
            currentPlot={plot}
        />

        const plotEditForm = plot ? <LocalForm
            model="plot"
            onSubmit={plot =>
                this.onSubmit(plot)
            }
            validateOn="submit"
            className="form col-12"
        >
            {
                this.renderFormRow('APC Code', <Control.text
                    model=".apcCode"
                    id="apcCode"
                    className={"form-control"}
                    maxLength={100}
                    required={false}
                    defaultValue={plot.apcCode}
                />)
            }

            {
                this.renderFormRow('Nearest Town/Suburb', <Control.text
                    model=".nearestSuburb"
                    id="nearestSuburb"
                    className={"form-control"}
                    maxLength={100}
                    required={false}
                    defaultValue={`${plot.nearestSuburb ? plot.nearestSuburb : ''}`}
                />)
            }

            {
                this.renderFormRow('Water Body', <Control.text
                    model=".waterBody"
                    id="waterBody"
                    className={"form-control"}
                    maxLength={100}
                    required={false}
                    defaultValue={`${plot.waterBody ? plot.waterBody : ''}`}
                />)
            }

            {
                this.renderFormRow('Site Name', <Control.text
                    model=".name"
                    id="name"
                    className={"form-control"}
                    maxLength={100}
                    required={true}
                    defaultValue={`${plot.name}`}
                />)
            }

            {
                this.renderFormRow('Latitude', <Control.text
                    model=".latitude"
                    id="latitude"
                    className={"form-control"}
                    maxLength={100}
                    required={true}
                    defaultValue={`${plot.latitude}`}
                />)
            }

            {
                this.renderFormRow('Longitude', <Control.text
                    model=".longitude"
                    id="longitude"
                    className={"form-control"}
                    maxLength={100}
                    required={true}
                    defaultValue={`${plot.longitude}`}
                />)
            }

            {
                this.renderFormRow('Private', <Control.checkbox
                    model=".private"
                    id="private"
                    className={"form-control form-check"}
                    defaultChecked={plot.private}
                />)
            }

            {
                this.renderFormRow('Site Deleted', <Control.checkbox
                    model=".obsolete"
                    id="obsolete"
                    className={"form-control form-check"}
                    defaultChecked={plot.obsolete}
                />)
            }

            {
                this.renderFormRow('Status', statusBadge)
            }

            {
                plot.plotStatus.code === "EVALUATING" ?
                    this.renderFormRow(`Set to not proposed`, <Control.checkbox
                        model=".setNotEvaluating"
                        id="setNotEvaluating"
                        className={"form-control form-check"}
                        defaultChecked={false}
                    />) : undefined
            }

            <button className="btn btn-lg btn-primary my-3" disabled={saving}>Save Changes</button>

        </LocalForm> : undefined;

        return (
            <>
                {plotEditForm}
                <div className="col-12">
                    <h3>Site Photo</h3>
                    {
                        plotPhotoUploading
                            ? <div className="p-5">
                                <Loader type="ball-scale-multiple" active />
                                <div className="ml-4 small">Uploading photo</div>
                            </div>
                            : <>
                                {
                                    plot.plotPhoto
                                        ? <FileImageThumbnail file={plot.plotPhoto} />
                                        : undefined
                                }
                                <PlotPhotoUpload
                                    plotId={plot.id}
                                    uploadPlotPhotos={uploadPlotPhotos}
                                />
                            </>
                    }

                </div>
                <ModalMessage
                    onRequestClose={this.onDeletePlotHideModel}
                    onNegativeResponse={this.onDeletePlotHideModel}
                    onPositiveResponse={this.onDeletePlotItem}
                    isOpen={this.state.showDeletePlotModal}
                    label="Delete Site"
                    message="Are you sure you want to delete this site?"

                    positiveResponseLabel="Delete"
                    positiveResponseClass="btn-danger"
                    negativeResponseLabel="Cancel"
                    negativeResponseClass="btn-secondary"
                />
            </>
        );
    }
}

const mapStateToProps = (state: IAppState, ownProps: PlotAdminEditAllProps): PlotAdminEditPropsMapped => {

    return {
        plotPhotoUploading: state.data.plotPhotoUploading,
        saving: state.data.plotSaving,
        referenceData: state.referenceData,
    }
}

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators<PlotAdminEditActionsMapped>({
        updatePlot: DashboardActionCreators.updatePlotRequest,
        uploadPlotPhotos: DashboardActionCreators.uploadPlotPhotosRequest,
    }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(PlotAdminEdit);